/**
 * Public-facing hub pages + event landing + registration flow.
 * Verbatim port of screens-public-hub.jsx: BrowserChrome, PublicNav, PublicFooter,
 * PublicEventCard, PublicLocationHub, PublicGroupHub, PublicWorkspaceHub,
 * PublicEventLanding, PublicEventRegister.
 */
import { useState, useEffect, useRef, type ReactNode } from "react";
import { Btn, Card, Checkbox, Chip, Icon, Input, T, alpha } from "@v0y4ge/jrni-ui";
import { useNav } from "../../shell/router";
import { useUser, isSignedIn } from "../../shell/access";
import { useEvents } from "../events/useEvents";
import { useHubs } from "./hubs-data";
import { toast } from "../../shell/toast";
import { publicHost, publicUrl as composePublicUrl } from "../../lib/public-url";
import { studioRequestOptions } from "../../lib/studio-config";
import { SETTINGS_OPTS } from "../../shell/request-opts";
import { openPublicEventPage } from "../events/eventActions";
import { getPublicBrandingTheme } from "@v0y4ge/api-client";
import { LandingPreview, LANDING_THEMES, themeFromBrandKit, type LandingBlock, type LandingTheme, type PublicEventDetailLike } from "../landing/landing-builder-helpers";
import { defaultLandingBlocks, previewEventFromCfg } from "../landing/LandingBuilder";
import * as ApiClient from "@v0y4ge/api-client";
import { useT } from "../../lib/i18n";

/**
 * PUBLIC_DARK / MAC_DOTS — ui-raw: the two fixed palettes this public surface
 * needs that the design system deliberately does not carry.
 *
 * These are NOT theme colours and must NOT be tokens. The public series hub and
 * the BrowserChrome mock render a FIXED dark presentation that is the same in
 * light and dark Studio (its `dark` prop is a per-surface design choice, not the
 * app theme), so every semantic `--jrni-*` token is wrong here by construction —
 * they all invert. Checked against jrni-tokens: only `#C18FE8` had an exact,
 * non-inverting token (`T.tPink`, adopted above); `#14102E` matches (ui-raw: hex in prose)
 * `--jrni-color-cover-bg` but that variable IS redefined per theme/tenant, so
 * binding to it would drift. MAC_DOTS are the macOS window-button colours — a
 * literal depiction of another product's chrome, vendor data like a provider
 * logo, never a token.
 *
 * Hoisted here (2026-08-11) because the same 8 values were re-typed across 30+
 * style objects in five components: BrowserChrome, PublicNav, PublicFooter,
 * PublicEventCard and PublicGroupHub each carried their own copy of the dark
 * border and the dark muted text. One declaration is the design-system answer
 * for values a token cannot express — the same reason `toneColors` exists rather
 * than a private tone map per component. If these ever become real tokens, this
 * const is the single place that changes.
 */
const PUBLIC_DARK = { // ui-raw: FIXED public-surface palette (see the note above) — deliberately not tokens
  /** Page ink — the series-hub canvas + footer. */
  page: "#0E0B1F",  // ui-raw: see the PUBLIC_DARK note above
  /** BrowserChrome viewport body. */
  chromeBody: "#14102E",  // ui-raw: see the PUBLIC_DARK note above
  /** BrowserChrome title bar + dark event card surface + hero gradient stop. */
  chromeBar: "#1A1530",  // ui-raw: see the PUBLIC_DARK note above
  /** Hairline on a dark surface (the dark counterpart of T.border). */
  line: "#2A2444",  // ui-raw: see the PUBLIC_DARK note above
  /** Stronger hairline — the chrome URL field only. */
  lineStrong: "#3A3460",  // ui-raw: see the PUBLIC_DARK note above
  /** Muted body text on a dark surface (the dark counterpart of T.soft). */
  muted: "#A1A0B5",  // ui-raw: see the PUBLIC_DARK note above
  /** Primary text on a dark surface (the dark counterpart of T.n1). */
  ink: "#F4F2FE",  // ui-raw: see the PUBLIC_DARK note above
  /** Low-availability / near-capacity warm accent (no token equivalent: the
   *  tertiary-orange token is #EE9D4F, a visibly different hue). */
  lowStock: "#FF6B47",  // ui-raw: see the PUBLIC_DARK note above
  /** Radial glow behind the series hero. */
  heroGlow: "rgba(137,85,242,0.25)",
  /** White washes for surfaces/borders/ink on the fixed dark hero. */
  // Note for the ink ON these fixed-dark surfaces: use `T.onColor`, never
  // `T.white`. `T.white` is --jrni-color-base-white, a SURFACE token that is
  // redefined to #212633 under [data-theme="dark"], so light ink written as (ui-raw: hex in prose)
  // T.white turns near-black on a surface that stayed dark (measured 2026-08-11
  // on the series hub: the 64px hero headline fell to 1.25:1 and the stat values
  // to 1.08:1 in dark). `T.onColor` is --jrni-color-on-brand, fixed #FFFFFF in (ui-raw: hex in prose)
  // both themes. `T.white` as an actual BACKGROUND here is correct and stays.
  wash04: "rgba(255,255,255,0.04)",
  wash06: "rgba(255,255,255,0.06)",
  wash08: "rgba(255,255,255,0.08)",
  wash15: "rgba(255,255,255,0.15)",
  wash55: "rgba(255,255,255,0.55)",
  wash85: "rgba(255,255,255,0.85)",
} as const;

/** macOS window-button colours, in order. Vendor chrome, not design tokens. */
const MAC_DOTS = ["#FF5F57", "#FEBC2E", "#28C840"] as const; // ui-raw: macOS window-button colours — vendor chrome, no token exists

interface PublishedSurface {
  slug?: string;
  blocks?: LandingBlock[];
  event?: (PublicEventDetailLike & { font?: string }) | null;
  eventId?: string;
  /** 'published' | 'draft-preview' — new data-layer field; isPublished is the shipped fallback. */
  publishState?: string;
  isPublished?: boolean;
  venueGeo?: { lat?: number; lng?: number; formattedAddress?: string } | null;
  brandKit?: unknown;
  branding?: unknown;
}

/** Load a display font for the public landing (Google Fonts cut). No-ops on
 *  repeat calls; unknown families fall back through the font stack. */
const _lpFonts = new Set<string>();
function ensureLandingFont(family?: string) {
  if (!family || family === "DM Sans" || _lpFonts.has(family)) return;
  _lpFonts.add(family);
  const id = "lp-font-" + family.replace(/[^a-z0-9]+/gi, "-").toLowerCase();
  if (document.getElementById(id)) return;
  const link = document.createElement("link");
  link.id = id; link.rel = "stylesheet";
  link.href = `https://fonts.googleapis.com/css2?family=${encodeURIComponent(family).replace(/%20/g, "+")}:wght@400;500;600;700&display=swap`;
  document.head.appendChild(link);
}

/* ── Public hub record (UIR-22, Voyage #193) ───────────────────────
 * The public hub pages render ONLY what `getPublicHub` returns — the tenant's
 * hub record ({ hub, events }) — never baked sample content. An unconfigured
 * hub renders its honest empty state. Param may be a hub id (HubEditor
 * preview / list eyeball) or a slug; with no param, the tenant's first hub of
 * the surface's kind is shown (the rail's generic entry point). */
type PublicHubState = { status: "loading" | "ok" | "error"; hub: Record<string, any> | null; events: Record<string, unknown>[] };
function usePublicHub(kind: "location" | "group" | "workspace"): PublicHubState {
  const nav = useNav();
  const hubs = useHubs();
  const param = String((nav.params?.id ?? nav.params?.slug) ?? "");
  const match = hubs.find((h) => h.id === param || h.slug === param) ?? (param ? null : hubs.find((h) => h.kind === kind)) ?? null;
  const slug = match?.slug || param;
  const [state, setState] = useState<PublicHubState>({ status: "loading", hub: null, events: [] });
  useEffect(() => {
    if (!slug) { setState({ status: "ok", hub: null, events: [] }); return; }
    let live = true;
    setState({ status: "loading", hub: null, events: [] });
    ApiClient.getPublicHub(slug, studioRequestOptions("hubs") as { baseUrl: string })
      .then((r) => {
        if (!live) return;
        const rr = r as unknown as { hub?: Record<string, any>; events?: Record<string, unknown>[] };
        setState({ status: "ok", hub: rr?.hub ?? null, events: Array.isArray(rr?.events) ? rr.events : [] });
      })
      .catch((e: unknown) => { console.warn("getPublicHub failed:", e); if (live) setState({ status: "error", hub: null, events: [] }); });
    return () => { live = false; };
  }, [slug]);
  return state;
}

/** First month token from a PublicHubEvent date string ("Fri 12 Jun" → "Jun"). */
const hubEventMonth = (e: Record<string, unknown>): string => String(e.date || "").split(" ")[2] || "";

/** Real client-side .ics download for a confirmed registration — UI-local work,
 *  no op needed (replaces the old fake "Calendar invite downloading" toast). */
function downloadIcs(ev: { id?: string; title?: string; date?: string; time?: string; location?: string }) {
  const esc = (s: string) => s.replace(/[\\;,]/g, (m) => `\\${m}`).replace(/\n/g, "\\n");
  const lines = [
    "BEGIN:VCALENDAR", "VERSION:2.0", "PRODID:-//JRNI Studio//Public Event//EN", "BEGIN:VEVENT",
    `UID:${ev.id || Math.random().toString(36).slice(2)}@studio`,
    `SUMMARY:${esc(String(ev.title || "Event"))}`,
    ev.location ? `LOCATION:${esc(String(ev.location))}` : "",
    `DESCRIPTION:${esc(`${ev.date ?? ""} ${ev.time ?? ""}`.trim())}`,
    "END:VEVENT", "END:VCALENDAR",
  ].filter(Boolean);
  const blob = new Blob([lines.join("\r\n")], { type: "text/calendar" });
  const a = document.createElement("a");
  a.href = URL.createObjectURL(blob);
  a.download = `${String(ev.title || "event").toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/(^-|-$)/g, "") || "event"}.ics`;
  document.body.appendChild(a); a.click(); a.remove();
  setTimeout(() => URL.revokeObjectURL(a.href), 4000);
}

// ── Shared chrome + nav ─────────────────────────────────────────────────
/** "Back to Studio" — shown only to signed-in Studio users viewing a public
 *  surface from inside the app (real visitors never see it). Sits in the
 *  browser-window top bar; floats top-left on the chrome-less landing. */
function BackToStudio({ to = "events", floating = false }: { to?: string; floating?: boolean }) {
  const t = useT();
  const nav = useNav();
  const u = useUser();
  if (!u || !u.role) return null;
  const btn = (
    <Btn type="button" onClick={() => nav.go(to)} title={t("hubs.publicHubs.returnToStudio", "Return to Studio")}
      style={{ display: "inline-flex", alignItems: "center", gap: 6, padding: "4px 10px", borderRadius: T.radius.pill, border: `1px solid ${T.border}`, background: T.white, color: T.n1, fontFamily: T.font, fontSize: 12, fontWeight: 600, cursor: "pointer", whiteSpace: "nowrap", flexShrink: 0, boxShadow: floating ? T.shadowMd : "none", height: "auto" }}>
      <Icon.arrow dir="left" size={11} /> {t("hubs.publicHubs.backToStudio", "Back to Studio")}</Btn>
  );
  if (!floating) return btn;
  return <div style={{ position: "fixed", top: 10, left: 10, zIndex: 90 }}>{btn}</div>;
}

function BrowserChrome({ url, dark = false, backTo, children }: { url: string; dark?: boolean; backTo?: string; children: ReactNode }) {
  const bg  = dark ? PUBLIC_DARK.chromeBody : T.white;
  const bar = dark ? PUBLIC_DARK.chromeBar : T.n6;
  const txt = dark ? PUBLIC_DARK.muted : T.soft;
  return (
    <div style={{ display: "flex", flexDirection: "column", height: "100%", background: bg, overflow: "hidden" }}>
      <div style={{ display: "flex", alignItems: "center", gap: 8, padding: "10px 14px", background: bar, borderBottom: `1px solid ${dark ? PUBLIC_DARK.line : T.border}` }}>
        {MAC_DOTS.map((c) => <span key={c} style={{ width: 10, height: 10, borderRadius: T.radius.pill, background: c }}/>)}
        <BackToStudio to={backTo} />
        <span style={{ flex: 1, textAlign: "center", maxWidth: 400, margin: "0 auto", padding: "4px 12px", borderRadius: T.radius.control, background: dark ? PUBLIC_DARK.line : T.white, border: `1px solid ${dark ? PUBLIC_DARK.lineStrong : T.border}`, fontFamily: T.fontMono, fontSize: 11, color: txt, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{url}</span>
      </div>
      <div style={{ flex: 1, overflow: "auto" }}>{children}</div>
    </div>
  );
}

// Scroll pane for the chrome-less public landing (2026-07-14 fix): the app
// root is 100vh + overflow:hidden, and the full-bleed attend render has no
// BrowserChrome scroller — pages taller than the viewport clipped with no way
// to reach the registration form. tabIndex + focus-on-mount make arrow/PgDn
// keyboard scrolling work immediately (public-page expectation).
function PublicScrollPane({ children }: { children: ReactNode }) {
  const ref = useRef<HTMLDivElement>(null);
  useEffect(() => { ref.current?.focus({ preventScroll: true }); }, []);
  return (
    <>
      <BackToStudio floating />
    <div ref={ref} tabIndex={0} data-testid="event-public-scroll"
      style={{ height: "100%", overflowY: "auto", overflowX: "hidden", background: T.white }}>
      {children}
    </div>
    </>
  );
}

/** Public nav — the brand block renders the HUB RECORD's name (UIR-22): no
 *  baked tenant identity. "Get notified" / "Sign in" have NO declared public
 *  op → honest-disabled + declared (REQ-081-DRAFT), never a fake success. */
function PublicNav({ dark = false, brand, active }: { dark?: boolean; brand?: string; active?: string }) {
  const t = useT();
  const nav = useNav();
  const fg   = dark ? PUBLIC_DARK.ink : T.n1;
  const muted = dark ? PUBLIC_DARK.muted : T.soft;
  const label = brand || t("hubs.publicHubs.events", "Events");
  const initial = (label.trim()[0] || "•").toUpperCase();
  const links = ["Upcoming", "Locations", "Series"];
  const routeMap: Record<string, string> = { Upcoming: "hub.public.workspace", Locations: "hub.public.location", Series: "hub.public.group" };
  return (
    <header style={{ padding: "20px 64px", display: "flex", alignItems: "center", justifyContent: "space-between", borderBottom: `1px solid ${dark ? PUBLIC_DARK.line : T.border}` }}>
      <div onClick={() => nav.go("hub.public.workspace")} style={{ display: "flex", alignItems: "center", gap: 12, cursor: "pointer" }}>
        <span style={{ width: 30, height: 30, borderRadius: T.radius.control, background: T.p1, color: T.white, display: "inline-flex", alignItems: "center", justifyContent: "center", fontWeight: 800, fontSize: 14 }}>{initial}</span>
        <span style={{ fontSize: 17, fontWeight: 700, color: fg, letterSpacing: -0.2 }}>{label}</span>
      </div>
      <nav style={{ display: "flex", gap: 28 }}>
        {links.map(l => (
          <span key={l} onClick={() => nav.go(routeMap[l])}
            style={{ fontSize: 14, fontWeight: 500, color: active === l ? fg : muted, cursor: "pointer", borderBottom: active === l ? `2px solid ${T.p1}` : "2px solid transparent", paddingBottom: 4 }}>{l}</span>
        ))}
      </nav>
      <div style={{ display: "flex", gap: 10, alignItems: "center" }}>
        <span title={t("hubs.publicHubs.signInNotAvailableYet", "Sign-in isn't available yet — no public sign-in operation is declared (REQ-081).")} style={{ fontSize: 13, color: muted, cursor: "not-allowed", fontWeight: 500, opacity: 0.55 }}>{t("hubs.publicHubs.signIn", "Sign in")}</span>
        {/* Ink is `T.onColor` (fixed #FFFFFF), not `T.white`: this label sits on a (ui-raw: hex in prose)
            brand wash, and on the fixed-dark nav of the series hub `T.white` flipped
            to #212633 in dark theme — measured 1.75:1 against the composited (ui-raw: hex in prose)
            rgba(p1,40%)-over-#0E0B1F fill, i.e. invisible. onColor measures 8.65:1 (ui-raw: hex in prose)
            there and is byte-identical in light, where both tokens are #FFFFFF. (ui-raw: hex in prose) */}
        <Btn type="button" disabled title={t("hubs.publicHubs.getNotifiedNotAvailableYet", "Not available yet — no public subscribe operation is declared (REQ-081).")} style={{ padding: "8px 16px", borderRadius: T.radius.pill, background: alpha(T.p1, "66"), color: T.onColor, border: "none", fontSize: 13, fontWeight: 600, cursor: "not-allowed", fontFamily: T.font, height: "auto", opacity: 1 }}>{t("hubs.publicHubs.getNotified", "Get notified")}</Btn>
      </div>
    </header>
  );
}

/** Public footer — renders the HUB RECORD's identity (UIR-22): brand + hub
 *  description; the old baked location/series/company link columns are gone
 *  (nothing in the hub record backs them). */
function PublicFooter({ dark = false, brand = "", description = "" }: { dark?: boolean; brand?: string; description?: string }) {
  const t = useT();
  const fg   = dark ? PUBLIC_DARK.ink : T.n1;
  const muted = dark ? PUBLIC_DARK.muted : T.soft;
  const border = dark ? PUBLIC_DARK.line : T.border;
  const initial = (brand.trim()[0] || "•").toUpperCase();
  return (
    <footer style={{ padding: "40px 64px 28px", borderTop: `1px solid ${border}`, background: dark ? PUBLIC_DARK.page : T.white }}>
      {brand ? (
        <div>
          <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 12 }}>
            <span style={{ width: 28, height: 28, borderRadius: T.radius.control, background: T.p1, color: T.white, display: "inline-flex", alignItems: "center", justifyContent: "center", fontWeight: 800, fontSize: 13 }}>{initial}</span>
            <span style={{ fontSize: 15, fontWeight: 700, color: fg }}>{brand}</span>
          </div>
          {description ? <p style={{ fontSize: 13, color: muted, lineHeight: 1.5, maxWidth: 420, margin: 0 }}>{description}</p> : null}
        </div>
      ) : null}
      <div style={{ marginTop: brand ? 32 : 0, paddingTop: brand ? 20 : 0, borderTop: brand ? `1px solid ${border}` : "none", display: "flex", justifyContent: "space-between", fontSize: 12, color: muted }}>
        <span>{brand ? /* TODO(i18n): replace template-literal with t('hubs.publicHubs.text', { ... }) */`© ${new Date().getFullYear()} ${brand}` : ""}</span>
        <span>{t("hubs.publicHubs.poweredBy", "Powered by")}{" "}<strong style={{ color: fg }}>{t("hubs.publicHubs.jrni", "JRNI")}</strong></span>
      </div>
    </footer>
  );
}

function PublicEventCard({ ev, dark = false, featured = false }: { ev: Record<string, unknown>; dark?: boolean; featured?: boolean }) {
  const t = useT();
  const nav = useNav();
  const cardBg = dark ? PUBLIC_DARK.chromeBar : T.white;
  const border = dark ? PUBLIC_DARK.line : T.border;
  const fg     = dark ? PUBLIC_DARK.ink : T.n1;
  const muted  = dark ? PUBLIC_DARK.muted : T.soft;
  const dp = String(ev.date || "").split(" ");
  const day = dp[1] || "—"; const mon = (dp[2] || "").toUpperCase();
  const cap = Number(ev.capacity) || 0;
  const reg = Number(ev.registered) || 0;
  const sold = cap ? Math.round((reg / cap) * 100) : 0;
  const spotsLeft = Math.max(0, cap - reg);
  const soldOut = spotsLeft === 0;
  const lowAvail = spotsLeft > 0 && spotsLeft <= 10;
  const hue = String(ev.hue || T.p1);
  return (
    <article onClick={() => nav.go("event.public", { eventId: String(ev.id || "") })}
      style={{ background: cardBg, borderRadius: 16, border: `1px solid ${border}`, overflow: "hidden", display: "flex", flexDirection: "column", cursor: "pointer", gridColumn: featured ? "span 2" : "span 1" }}>
      <div style={{ height: featured ? 280 : 180, position: "relative", background: `linear-gradient(135deg, ${hue}, ${alpha(hue, "88")}), repeating-linear-gradient(45deg, ${PUBLIC_DARK.wash08} 0 12px, transparent 12px 24px)`, backgroundBlendMode: "overlay" }}>
        <span style={{ position: "absolute", top: 12, left: 12, background: T.white, color: T.n1, padding: "8px 12px", borderRadius: T.radius.md, fontWeight: 700, fontSize: 13, display: "flex", flexDirection: "column", alignItems: "center", lineHeight: 1.1, minWidth: 44 }}>
          <span style={{ fontSize: 10, color: T.p1, fontWeight: 700, letterSpacing: 0.5 }}>{mon}</span>
          <span style={{ fontSize: 22, fontWeight: 800, color: T.n1 }}>{day}</span>
        </span>
        {soldOut ? <span style={{ position: "absolute", top: 12, right: 12, background: T.n1, color: T.white, padding: "5px 10px", borderRadius: T.radius.pill, fontSize: 11, fontWeight: 700, textTransform: "uppercase" }}>{t("hubs.publicHubs.soldOut", "Sold out")}</span>
         : lowAvail ? <span style={{ position: "absolute", top: 12, right: 12, background: PUBLIC_DARK.lowStock, color: T.white, padding: "5px 10px", borderRadius: T.radius.pill, fontSize: 11, fontWeight: 700, textTransform: "uppercase" }}>{spotsLeft} {t("hubs.publicHubs.left", "left")}</span> : null}
      </div>
      <div style={{ padding: 20, display: "flex", flexDirection: "column", gap: 10, flex: 1 }}>
        <div style={{ display: "flex", gap: 6, alignItems: "center" }}>
          <span style={{ fontSize: 11, fontWeight: 600, color: T.p1, textTransform: "uppercase", letterSpacing: 0.5 }}>{String(ev.template || "Event")}</span>
          <span style={{ width: 3, height: 3, borderRadius: T.radius.pill, background: muted }}/>
          <span style={{ fontSize: 11, color: muted }}>{String(ev.time || "")}</span>
        </div>
        <h3 style={{ fontSize: featured ? 26 : 19, fontWeight: 700, color: fg, lineHeight: 1.25, margin: 0, letterSpacing: -0.3 }}>{String(ev.title || "")}</h3>
        <div style={{ display: "flex", alignItems: "center", gap: 6, color: muted, fontSize: 13, marginTop: "auto", paddingTop: 6 }}>
          <Icon.loc size={12}/><span style={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{String(ev.location || "")}</span>
        </div>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", paddingTop: 10, borderTop: `1px solid ${border}` }}>
          <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
            <div style={{ height: 4, width: 50, background: dark ? PUBLIC_DARK.line : T.n5, borderRadius: T.radius.pill, overflow: "hidden" }}>
              <div style={{ height: "100%", width: `${Math.min(100, sold)}%`, background: soldOut ? T.n1 : sold >= 80 ? PUBLIC_DARK.lowStock : T.p1 }}/>
            </div>
            <span style={{ fontSize: 11, color: muted }}>{reg} / {cap}</span>
          </div>
          <span style={{ display: "inline-flex", alignItems: "center", gap: 4, fontSize: 13, fontWeight: 600, color: T.p1 }}>{soldOut ? t('hubs.publicHubs.text2') : t('hubs.publicHubs.text3')} <Icon.arrow size={11}/></span>
        </div>
      </div>
    </article>
  );
}

// ── Hub screens ────────────────────────────────────────────────────────
export function PublicLocationHub() {
  const t = useT();
  const { status, hub, events } = usePublicHub("location");
  const upcoming = events.filter(e => String(e.status) !== "past");
  const [formatChip, setFormatChip] = useState("All");
  const [month, setMonth] = useState("All");
  const formats = ["All", ...Array.from(new Set(upcoming.map(e => String(e.template || "")).filter(Boolean)))];
  const months = ["All", ...Array.from(new Set(upcoming.map(hubEventMonth).filter(Boolean)))];
  const filtered = upcoming.filter(ev =>
    (formatChip === "All" || String(ev.template || "") === formatChip) &&
    (month === "All" || hubEventMonth(ev) === month));
  const url = String(hub?.publicUrl || (hub?.slug ? composePublicUrl(String(hub.slug), { bare: true }) : publicHost("attend")));
  const pane = (children: ReactNode) => <BrowserChrome url={url} backTo="hub.pages">{children}</BrowserChrome>;
  if (status === "loading") return pane(<div style={{ padding: 80, textAlign: "center", color: T.soft, fontSize: 15 }}>{t("common.status.loading", "Loading…")}</div>);
  if (status === "error") return pane(<div style={{ padding: 80, textAlign: "center", color: T.soft, fontSize: 15 }}>{t("hubs.publicHubs.couldnTLoadThisHub", "Couldn't load this hub. Retry in a moment.")}</div>);
  if (!hub) return pane(<div style={{ padding: 80, textAlign: "center", color: T.soft, fontSize: 15 }}>{t("hubs.publicHubs.thisHubIsnTAvailable", "This hub isn't available.")}</div>);
  const headline = String(hub.brandHeadline || hub.name || "");
  const subhead = String(hub.brandSubhead || hub.description || "");
  const heroImg = String(hub.brandHeroImageUrl || "");
  return pane(
    <>
      <PublicNav brand={String(hub.name || "")} active="Locations"/>
      <section style={{ padding: "48px 64px 36px", background: T.white }}>
        <div style={{ display: "flex", gap: 48, alignItems: "flex-start" }}>
          <div style={{ flex: 1 }}>
            <div style={{ fontSize: 12, fontWeight: 700, color: T.p1, textTransform: "uppercase", letterSpacing: 0.8, marginBottom: 14, display: "flex", alignItems: "center", gap: 8 }}><Icon.loc size={12}/> {String(hub.name || "")}</div>
            <h1 style={{ fontSize: 52, fontWeight: 800, color: T.n1, lineHeight: 1.05, letterSpacing: -1.2, margin: 0 }}>{headline}</h1>
            {subhead ? <p style={{ fontSize: 17, color: T.soft, lineHeight: 1.55, marginTop: 18, maxWidth: 560 }}>{subhead}</p> : null}
            <div style={{ display: "flex", gap: 24, marginTop: 28, flexWrap: "wrap" }}>
              <div><div style={{ fontSize: 11, color: T.soft, fontWeight: 600, textTransform: "uppercase", letterSpacing: 0.5 }}>{t("hubs.publicHubs.upcomingEvents", "Upcoming events")}</div><div style={{ fontSize: 14, color: T.n1, fontWeight: 600, marginTop: 4 }}>{upcoming.length}</div></div>
            </div>
          </div>
          {heroImg ? (
            <img src={heroImg} alt="" style={{ width: 420, height: 320, borderRadius: 16, objectFit: "cover", border: `1px solid ${T.border}`, flexShrink: 0 }}/>
          ) : (
            <div style={{ width: 420, height: 320, borderRadius: 16, background: `linear-gradient(135deg, ${alpha(T.tLightBlue, "33")}, ${T.p3})`, border: `1px solid ${T.border}`, flexShrink: 0, display: "flex", alignItems: "center", justifyContent: "center" }}>
              <div style={{ textAlign: "center", display: "flex", flexDirection: "column", alignItems: "center", gap: 8 }}>
                <span style={{ width: 36, height: 36, borderRadius: T.radius.pill, background: T.p1, color: T.white, display: "inline-flex", alignItems: "center", justifyContent: "center" }}><Icon.loc size={18}/></span>
                <span style={{ fontSize: 12, fontWeight: 700, color: T.n1, background: T.white, padding: "4px 10px", borderRadius: T.radius.sm }}>{String(hub.name || "")}</span>
              </div>
            </div>
          )}
        </div>
      </section>
      {upcoming.length > 0 ? (
        <section style={{ padding: "20px 64px", background: T.n6, borderTop: `1px solid ${T.border}`, borderBottom: `1px solid ${T.border}` }}>
          <div style={{ display: "flex", gap: 8, alignItems: "center", flexWrap: "wrap" }}>
            <span style={{ fontSize: 12, color: T.soft, fontWeight: 600, textTransform: "uppercase", letterSpacing: 0.5, marginRight: 4 }}>{t("hubs.publicHubs.filter", "Filter")}</span>
            {formats.map((f) => (
              <Chip key={f} selected={formatChip === f} onClick={() => setFormatChip(f)} style={{ padding: "7px 14px", borderRadius: T.radius.pill, fontSize: 13, fontWeight: 500, background: formatChip === f ? T.n1 : T.white, color: formatChip === f ? T.white : T.n1, border: `1px solid ${formatChip === f ? T.n1 : T.borderStrong}`, cursor: "pointer", fontFamily: T.font }}>{f}</Chip>
            ))}
            <span style={{ flex: 1 }}/>
            {/* ui-raw: the native month filter below. Swapping it to jrni-ui Dropdown was
                BUILT AND MEASURED (2026-08-11): the custom trigger is 36px tall against this
                native select's 31px, so the filter band grew and the page went 2175px →
                2180px; the caret and control width change too. Real drift, so the swap was
                reverted rather than shipped — see the MISSING list. */}
            {months.length > 1 ? (
              <select /* ui-raw: native 31px month filter; measured swap grew the page (reasoning above) */ value={month} onChange={(e) => setMonth(e.target.value)} style={{ padding: "7px 12px", borderRadius: T.radius.control, border: `1px solid ${T.borderStrong}`, background: T.white, fontSize: 13, color: T.n1, fontFamily: T.font }}>
                {months.map((mo) => <option key={mo}>{mo}</option>)}
              </select>
            ) : null}
          </div>
        </section>
      ) : null}
      <section style={{ padding: "40px 64px", background: T.canvas }}>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-end", marginBottom: 20 }}>
          <h2 style={{ fontSize: 28, fontWeight: 700, color: T.n1, margin: 0, letterSpacing: -0.5 }}>{t("hubs.publicHubs.upcoming", "Upcoming ·")}{filtered.length}</h2>
        </div>
        <div style={{ display: "grid", gridTemplateColumns: "repeat(3, 1fr)", gap: 20 }}>
          {filtered.map((ev, i) => <PublicEventCard key={String(ev.id)} ev={ev} featured={i === 0 && filtered.length > 2}/>)}
        </div>
        {upcoming.length === 0 ? (
          /* jrni-ui InlineEmptyState was BUILT AND MEASURED here (2026-08-11) and REVERTED:
             its title renders at T.text.body's 1.5 line-height where this box inherits
             `normal`, so the box grew 93px -> 95px and the text shifted 1px down —
             17,370 differing pixels. It is the right target (it also adds the
             role="status" announcement this box lacks) the moment that 2px is accepted.
             See the MISSING list. */
          <div style={{ padding: 36, textAlign: "center", color: T.soft, fontSize: 14, background: T.white, borderRadius: 16, border: `1px solid ${T.border}` }}>{t("hubs.publicHubs.thisHubHasNoPublishedEventsYet", "This hub has no published events yet.")}</div>
        )
         : filtered.length === 0 ? <div style={{ padding: 36, textAlign: "center", color: T.soft, fontSize: 14, background: T.white, borderRadius: 16, border: `1px solid ${T.border}` }}>{t("hubs.publicHubs.noEventsMatchTheseFilters", "No events match these filters.")}</div> : null}
      </section>
      <PublicFooter brand={String(hub.name || "")} description={String(hub.description || "")}/>
    </>
  );
}

export function PublicGroupHub() {
  const t = useT();
  const { status, hub, events } = usePublicHub("group");
  const [cityChip, setCityChip] = useState("All");
  const cities = Array.from(new Set(events.map((e) => String(e.location || "").split(" · ")[0]).filter(Boolean)));
  const chips = ["All", ...cities.slice(0, 6)];
  const filtered = cityChip === "All" ? events : events.filter(e => String(e.location || "").includes(cityChip));
  const totalSeats = events.reduce((s, e) => s + (Number(e.capacity) || 0), 0);
  const monthTokens = events.map(hubEventMonth).filter(Boolean);
  const monthSpan = monthTokens.length ? (monthTokens[0] === monthTokens[monthTokens.length - 1] ? monthTokens[0] : `${monthTokens[0]} — ${monthTokens[monthTokens.length - 1]}`) : "";
  const url = String(hub?.publicUrl || (hub?.slug ? composePublicUrl(String(hub.slug), { bare: true }) : publicHost("public")));
  const pane = (children: ReactNode) => <BrowserChrome url={url} dark backTo="hub.pages">{children}</BrowserChrome>;
  if (status === "loading") return pane(<div style={{ padding: 80, textAlign: "center", color: PUBLIC_DARK.muted, fontSize: 15 }}>{t("common.status.loading", "Loading…")}</div>);
  if (status === "error") return pane(<div style={{ padding: 80, textAlign: "center", color: PUBLIC_DARK.muted, fontSize: 15 }}>{t("hubs.publicHubs.couldnTLoadThisHub", "Couldn't load this hub. Retry in a moment.")}</div>);
  if (!hub) return pane(<div style={{ padding: 80, textAlign: "center", color: PUBLIC_DARK.muted, fontSize: 15 }}>{t("hubs.publicHubs.thisHubIsnTAvailable", "This hub isn't available.")}</div>);
  const headline = String(hub.brandHeadline || hub.name || "");
  const subhead = String(hub.brandSubhead || hub.description || "");
  // Series hero artwork (2026-08-14): the group surface never consumed
  // brandHeroImageUrl — only location/workspace did — so department hubs
  // rendered the fixed glow with no hero. Rendered as a low-opacity full-bleed
  // layer UNDER the glow: the dark series identity keeps its contrast (the 64px
  // headline sits on the same dark base), while the hub's artwork tints the
  // hero. Location/workspace render it as a photo panel instead; both read the
  // same field, so swapping in real photography changes no layout.
  const heroImg = String(hub.brandHeroImageUrl || "");
  const stats: { k: string; v: string }[] = events.length ? [
    { k: String(events.length), v: t("hubs.publicHubs.sessions", "Sessions") },
    ...(cities.length ? [{ k: String(cities.length), v: t("hubs.publicHubs.cities", "Cities") }] : []),
    ...(totalSeats ? [{ k: totalSeats.toLocaleString(), v: t("hubs.publicHubs.seats", "Seats") }] : []),
    ...(monthSpan ? [{ k: monthSpan, v: t("hubs.publicHubs.running", "Running") }] : []),
  ] : [];
  return pane(
    <>
      <PublicNav dark brand={String(hub.name || "")} active="Series"/>
      <section style={{ padding: "80px 64px 60px", background: `linear-gradient(180deg, ${PUBLIC_DARK.page} 0%, ${PUBLIC_DARK.chromeBar} 100%)`, position: "relative", overflow: "hidden" }}>
        {heroImg ? <div aria-hidden="true" style={{ position: "absolute", inset: 0, background: `url(${heroImg}) center/cover`, opacity: 0.28 }}/> : null}
        <div style={{ position: "absolute", inset: 0, background: `radial-gradient(circle at 70% 30%, ${PUBLIC_DARK.heroGlow}, transparent 60%)` }}/>
        <div style={{ position: "relative", display: "grid", gridTemplateColumns: stats.length ? "1.3fr 1fr" : "1fr", gap: 60, alignItems: "center" }}>
          <div>
            {/* T.tPink IS #C18FE8 (--jrni-color-tertiary-pink), one of the FIXED tertiary
                tokens (no [data-theme="dark"] override), so the solid accent + dot tokenize
                with zero pixel change in either theme. The two translucent washes stay raw
                rgba on purpose: `alpha()` emits `color-mix(in srgb, … 15%, transparent)`,
                which was RENDERED AND MEASURED here as 370 differing pixels against the
                rgba() original — color-mix rounds differently. ui-raw: keeping the exact
                rgba washes is the parity-preserving choice. */}
            <div style={{ display: "inline-flex", alignItems: "center", gap: 8, padding: "6px 14px", background: "rgba(193,143,232,0.15)", border: "1px solid rgba(193,143,232,0.3)", borderRadius: T.radius.pill, fontSize: 12, fontWeight: 600, color: T.tPink, textTransform: "uppercase", letterSpacing: 0.6, marginBottom: 20 }}>
              <span style={{ width: 6, height: 6, borderRadius: T.radius.pill, background: T.tPink }}/> {t("hubs.publicHubs.series", "Series ·")}{events.length} {t("hubs.publicHubs.sessions82a01e", "sessions")}</div>
            <h1 style={{ fontSize: 64, fontWeight: 800, color: T.onColor, lineHeight: 1, letterSpacing: -1.8, margin: "0 0 24px" }}>{headline}</h1>
            {subhead ? <p style={{ fontSize: 18, color: PUBLIC_DARK.muted, lineHeight: 1.55, maxWidth: 560 }}>{subhead}</p> : null}
            <div style={{ display: "flex", gap: 12, marginTop: 32 }}>
              <Btn type="button" disabled title={t("hubs.publicHubs.subscribeNotAvailableYet", "Not available yet — no public subscribe operation is declared (REQ-081).")} style={{ padding: "14px 24px", borderRadius: T.radius.pill, background: PUBLIC_DARK.wash55, color: PUBLIC_DARK.page, border: "none", fontSize: 14, fontWeight: 700, cursor: "not-allowed", fontFamily: T.font, height: "auto", opacity: 1 }}>{t("hubs.publicHubs.subscribeToTheSeries", "Subscribe to the series")}</Btn>
            </div>
          </div>
          {stats.length ? (
            <div style={{ background: PUBLIC_DARK.wash04, border: `1px solid ${PUBLIC_DARK.wash08}`, borderRadius: 20, padding: 28 }}>
              <div style={{ fontSize: 11, color: PUBLIC_DARK.muted, fontWeight: 700, textTransform: "uppercase", letterSpacing: 0.8, marginBottom: 16 }}>{t("hubs.publicHubs.seriesAtAGlance", "Series at a glance")}</div>
              {stats.map((row, i) => (
                <div key={i} style={{ display: "flex", justifyContent: "space-between", alignItems: "baseline", padding: "14px 0", borderBottom: i < stats.length - 1 ? `1px solid ${PUBLIC_DARK.wash06}` : "none" }}>
                  <span style={{ fontSize: 32, fontWeight: 800, color: T.onColor, letterSpacing: -0.5 }}>{row.k}</span>
                  <span style={{ fontSize: 13, color: PUBLIC_DARK.muted }}>{row.v}</span>
                </div>
              ))}
            </div>
          ) : null}
        </div>
      </section>
      {chips.length > 1 ? (
        <section style={{ padding: "28px 64px 0", background: PUBLIC_DARK.page }}>
          <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
            <span style={{ fontSize: 12, color: PUBLIC_DARK.muted, fontWeight: 600, textTransform: "uppercase", letterSpacing: 0.5, marginRight: 4 }}>{t("hubs.publicHubs.city", "City")}</span>
            {chips.map((c) => (
              <Chip key={c} selected={cityChip === c} onClick={() => setCityChip(c)} style={{ padding: "8px 16px", borderRadius: T.radius.pill, fontSize: 13, fontWeight: 500, background: cityChip === c ? T.white : "transparent", color: cityChip === c ? PUBLIC_DARK.page : T.onColor, border: `1px solid ${cityChip === c ? T.white : PUBLIC_DARK.wash15}`, cursor: "pointer", fontFamily: T.font }}>{c}</Chip>
            ))}
          </div>
        </section>
      ) : null}
      <section style={{ padding: "40px 64px 60px", background: PUBLIC_DARK.page }}>
        <h2 style={{ fontSize: 28, fontWeight: 700, color: T.onColor, margin: "0 0 24px", letterSpacing: -0.5 }}>{t("hubs.publicHubs.allSessions", "All sessions ·")}{filtered.length}</h2>
        {events.length === 0 ? <div style={{ padding: 36, textAlign: "center", color: PUBLIC_DARK.muted, fontSize: 14, background: PUBLIC_DARK.wash04, borderRadius: 16, border: `1px solid ${PUBLIC_DARK.wash08}` }}>{t("hubs.publicHubs.thisHubHasNoPublishedEventsYet", "This hub has no published events yet.")}</div> : (
          <div style={{ display: "grid", gridTemplateColumns: "repeat(3, 1fr)", gap: 20 }}>
            {filtered.map(ev => <PublicEventCard key={String(ev.id)} ev={ev} dark/>)}
          </div>
        )}
      </section>
      <PublicFooter dark brand={String(hub.name || "")} description={String(hub.description || "")}/>
    </>
  );
}

export function PublicWorkspaceHub() {
  const t = useT();
  const nav = useNav();
  const { status, hub, events } = usePublicHub("workspace");
  const featuredId = String(hub?.layout?.featuredEventId ?? "");
  const featured = (featuredId ? events.find((e) => String(e.id) === featuredId) : undefined) ?? events[0];
  const rest = events.filter((e) => e !== featured);
  const [q, setQ] = useState("");
  const [locChip, setLocChip] = useState("All");
  const locChips = ["All", ...Array.from(new Set(rest.map((e) => String(e.location || "").split(" · ")[0]).filter(Boolean))).slice(0, 5)];
  const filtered = rest.filter(ev => {
    if (locChip !== "All" && !String(ev.location || "").includes(locChip)) return false;
    if (!q.trim()) return true;
    return `${ev.title} ${ev.host} ${ev.location} ${ev.template || ""}`.toLowerCase().includes(q.trim().toLowerCase());
  });
  const url = String(hub?.publicUrl || (hub?.slug ? composePublicUrl(String(hub.slug), { bare: true }) : publicHost("attend")));
  const pane = (children: ReactNode) => <BrowserChrome url={url} backTo="hub.pages">{children}</BrowserChrome>;
  if (status === "loading") return pane(<div style={{ padding: 80, textAlign: "center", color: T.soft, fontSize: 15 }}>{t("common.status.loading", "Loading…")}</div>);
  if (status === "error") return pane(<div style={{ padding: 80, textAlign: "center", color: T.soft, fontSize: 15 }}>{t("hubs.publicHubs.couldnTLoadThisHub", "Couldn't load this hub. Retry in a moment.")}</div>);
  if (!hub) return pane(<div style={{ padding: 80, textAlign: "center", color: T.soft, fontSize: 15 }}>{t("hubs.publicHubs.thisHubIsnTAvailable", "This hub isn't available.")}</div>);
  const heroImg = String(hub.brandHeroImageUrl || "");
  const fTickets = (featured?.tickets ?? []) as { priceMinor?: number }[];
  const featuredFree = fTickets.length === 0 || fTickets.every((tk) => !tk.priceMinor);
  return pane(
    <>
      <PublicNav brand={String(hub.name || "")} active="Upcoming"/>
      <section style={{ padding: "56px 64px 40px", background: T.white, borderBottom: `1px solid ${T.border}` }}>
        {featured ? (
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1.1fr", gap: 56, alignItems: "center" }}>
            <div>
              <div style={{ fontSize: 12, fontWeight: 700, color: T.p1, textTransform: "uppercase", letterSpacing: 0.8 }}>{t("hubs.publicHubs.featured", "Featured")}</div>
              <h1 style={{ fontSize: 56, fontWeight: 800, color: T.n1, lineHeight: 1.05, letterSpacing: -1.5, margin: "16px 0 0" }}>{String(featured.title || "")}</h1>
              <p style={{ fontSize: 17, color: T.soft, lineHeight: 1.55, marginTop: 18, maxWidth: 520 }}>{featured.host ? <>{t("hubs.publicHubs.hostedBy", "Hosted by")}{" "}{String(featured.host)}{featured.location ? <> · {String(featured.location)}</> : null}</> : String(featured.location || "")}</p>
              <div style={{ display: "flex", gap: 12, marginTop: 32 }}>
                {/* ui-raw: the glow below is rgba(81,66,199,0.3) — the DEFAULT tenant's
                    --jrni-color-primary-1 (#5142C7) frozen as a literal. It is left as-is (ui-raw: hex in prose)
                    only because both fixes drift: `alpha(T.p1, 0.3)` emits color-mix, which
                    was measured rounding differently from rgba() elsewhere in this file, and
                    T.p1 is tenant-swappable so the glow would also change hue per tenant.
                    That tenant behaviour is the CORRECT one and this literal is the bug —
                    under the burnt-orange tenant this button renders orange with a purple
                    halo. Needs a real brand-shadow token; see the MISSING list. */}
                <Btn onClick={() => nav.go("event.register", { eventId: String(featured.id || "") })} style={{ padding: "14px 28px", borderRadius: T.radius.md, background: T.p1, color: T.white, border: "none", fontSize: 15, fontWeight: 700, cursor: "pointer", fontFamily: T.font, boxShadow: "0 4px 14px rgba(81,66,199,0.3)", height: "auto" }}>{featuredFree ? t("hubs.publicHubs.registerFree", "Register · Free") : t("hubs.publicHubs.register", "Register")}</Btn>
                {/* variant="ghost" (not an inline `background: transparent`): the CSS state
                    layer's ghost hover tint only applies when the button leaves its
                    background to CSS — an inline background wins over the hover rule and
                    kills it. Renders identically; adds the missing hover feedback. */}
                <Btn variant="ghost" onClick={() => nav.go("event.public", { eventId: String(featured.id || "") })} style={{ padding: "14px 24px", borderRadius: T.radius.md, color: T.n1, border: `1px solid ${T.borderStrong}`, fontSize: 15, fontWeight: 600, cursor: "pointer", fontFamily: T.font, height: "auto" }}>{t("hubs.publicHubs.viewDetails", "View details")}</Btn>
              </div>
            </div>
            {heroImg ? (
              <img src={heroImg} alt="" style={{ height: 440, borderRadius: 20, width: "100%", objectFit: "cover", border: `1px solid ${T.border}` }}/>
            ) : (
              <div style={{ height: 440, borderRadius: 20, background: `linear-gradient(135deg, ${String(featured.hue || T.p1)}, ${T.p1}), repeating-linear-gradient(45deg, ${PUBLIC_DARK.wash06} 0 16px, transparent 16px 32px)`, backgroundBlendMode: "overlay", display: "flex", alignItems: "flex-end", padding: 20 }}>
                <span style={{ fontFamily: T.fontMono, fontSize: 10, color: PUBLIC_DARK.wash85, textTransform: "uppercase", letterSpacing: 0.5 }}>{t("hubs.publicHubs.heroPhoto1600900", "hero photo · 1600×900")}</span>
              </div>
            )}
          </div>
        ) : (
          <div style={{ padding: "48px 0", textAlign: "center", color: T.soft, fontSize: 15 }}>{t("hubs.publicHubs.thisHubHasNoPublishedEventsYet", "This hub has no published events yet.")}</div>
        )}
      </section>
      {rest.length > 0 ? (
        <section style={{ padding: "24px 64px", background: T.white, borderBottom: `1px solid ${T.border}` }}>
          <div style={{ display: "flex", gap: 12, alignItems: "center" }}>
            <div style={{ position: "relative", flex: 1, maxWidth: 480 }}>
              <span style={{ position: "absolute", left: 14, top: "50%", transform: "translateY(-50%)", color: T.soft }}><Icon.search size={14}/></span>
              {/* Pill-shaped public search field. jrni-ui Input CANNOT reproduce it: `style`
                  reaches only Input's OUTER wrapper (Input.tsx:89), while the inner control
                  row hardcodes height `T.space[10]` (40px), `borderRadius: T.radius.default`
                  and `background: T.white` (Input.tsx:91-92) — none of which a call site can
                  reach. Swapping turns the pill into a rounded-rect box and T.n6 into white.
                  ui-raw: left raw to preserve parity. Input needs an additive pill/search
                  shape tier (3 hand-rolled pill fields in the fleet: here + WorkforceMyDay ×2).

                  `boxSizing: "border-box"` is REQUIRED, not cosmetic. reset.css sets no
                  global box-sizing (documented there: "consumers that need border-box on
                  individual elements apply it locally"), so under the content-box default
                  `width: 100%` + 38px/14px inline padding + 2 borders rendered this field
                  534px wide inside its 480px wrapper — 54px of overflow that painted the
                  input's own dark pill over the first 29px of the "All" chip (the wrapper
                  is position:relative, so it stacks ABOVE the static chip buttons and won
                  the hit test). border-box makes the declared 480px the outer width. */}
              <input /* ui-raw: pill search field + required border-box (reasoning above) */ value={q} onChange={(e) => setQ(e.target.value)} placeholder={t("hubs.publicHubs.searchEventsHostsTopics", "Search events, hosts, topics…")} style={{ boxSizing: "border-box", width: "100%", padding: "12px 14px 12px 38px", borderRadius: T.radius.pill, border: `1px solid ${T.borderStrong}`, fontSize: 14, fontFamily: T.font, color: T.n1, background: T.n6 }}/>
            </div>
            {locChips.length > 1 ? <span style={{ width: 1, height: 24, background: T.border }}/> : null}
            {locChips.length > 1 ? locChips.map((c) => (
              <Chip key={c} selected={locChip === c} onClick={() => setLocChip(c)} style={{ padding: "8px 14px", borderRadius: T.radius.pill, fontSize: 13, fontWeight: 500, background: locChip === c ? T.n1 : T.white, color: locChip === c ? T.white : T.n1, border: `1px solid ${locChip === c ? T.n1 : T.borderStrong}`, cursor: "pointer", fontFamily: T.font }}>{c}</Chip>
            )) : null}
          </div>
        </section>
      ) : null}
      <section style={{ padding: "40px 64px", background: T.canvas }}>
        <h2 style={{ fontSize: 28, fontWeight: 700, color: T.n1, margin: "0 0 20px", letterSpacing: -0.5 }}>{t("hubs.publicHubs.allUpcoming", "All upcoming ·")}{filtered.length}</h2>
        {rest.length === 0 ? <div style={{ padding: 36, textAlign: "center", color: T.soft, fontSize: 14, background: T.white, borderRadius: 16, border: `1px solid ${T.border}` }}>{t("hubs.publicHubs.noFurtherUpcomingEvents", "No further upcoming events.")}</div>
         : filtered.length === 0 ? <div style={{ padding: 36, textAlign: "center", color: T.soft, fontSize: 14, background: T.white, borderRadius: 16, border: `1px solid ${T.border}` }}>{t("hubs.publicHubs.noEventsMatchYourSearch", "No events match your search.")}</div>
         : <div style={{ display: "grid", gridTemplateColumns: "repeat(3, 1fr)", gap: 20 }}>{filtered.map(ev => <PublicEventCard key={String(ev.id)} ev={ev}/>)}</div>}
      </section>
      <PublicFooter brand={String(hub.name || "")} description={String(hub.description || "")}/>
    </>
  );
}

// ── Event landing + register ────────────────────────────────────────────
// §2.5 (packet 2026-07-26): the anonymous public path must fire ZERO
// admin-scoped calls — useEvents() hits /v1/admin/events. The hook therefore
// lives ONLY in the authenticated builder/eyeball preview component (eventId
// param); the anonymous slug component never mounts it (two components, so no
// conditional hook call). Acceptance per route type: anonymous /attend/:slug
// load → no /v1/admin/* request; authenticated eyeball/wizard preview
// (eventId param) → admin events cache allowed. Audit of other admin-scoped
// reads under public attend routes: PublicEventRegister (below) also consumes
// useEvents — flagged to Voyage for the same split; hub pages + queue
// join/display already use public ops only.
export function PublicEventLanding() {
  const nav = useNav();
  const slugParam = String(nav.params?.slug ?? "");
  const idParam = String((nav.params?.eventId || nav.params?.id) ?? "");
  return idParam ? <AdminPreviewEventLanding idParam={idParam} /> : <PublicSlugEventLanding slugParam={slugParam} />;
}
function PublicSlugEventLanding({ slugParam }: { slugParam: string }) {
  return <EventLandingBody slugParam={slugParam} idParam="" row={null} eventsLoading={false} />;
}
function AdminPreviewEventLanding({ idParam }: { idParam: string }) {
  const { events, loading: eventsLoading } = useEvents();
  const row = events.find((e) => e.id === idParam) ?? null;
  return <EventLandingBody slugParam="" idParam={idParam} row={row} eventsLoading={eventsLoading} />;
}
// Two resolution paths (2026-07-14 — every events-list eyeball 404'd because
// the EVENT ID was fed into the slug-keyed published-surface lookup):
//   • slug param (public-hub links)  → getPublishedLandingSurface(slug).
//   • eventId/id param (eyeball, wizard Preview) → getLandingPage(eventId) —
//     the SAME op + saved block array the Landing builder step edits, so this
//     preview IS the builder's live render (shared LandingPreview +
//     defaultLandingBlocks + previewEventFromCfg), and it works for drafts
//     that have no published surface yet.
function EventLandingBody({ slugParam, idParam, row, eventsLoading }: { slugParam: string; idParam: string; row: ReturnType<typeof useEvents>["events"][number] | null; eventsLoading: boolean }) {
  const t = useT();
  const [state, setState] = useState<{ status: "loading" | "ok" | "error"; surface: PublishedSurface | null; page: Record<string, any> | null }>({ status: "loading", surface: null, page: null });
  // Tenant brand kit for the landing theme (voyage#59) — fetched once; null
  // until it resolves or when the tenant has no kit (honest fallback below).
  const [brandTheme, setBrandTheme] = useState<LandingTheme | null>(null);
  useEffect(() => {
    let live = true;
    getPublicBrandingTheme(SETTINGS_OPTS)
      .then((r) => { if (live) setBrandTheme(themeFromBrandKit((r as { theme?: unknown })?.theme)); })
      .catch(() => { /* no tenant kit — keep the preset fallback */ });
    return () => { live = false; };
  }, []);
  useEffect(() => {
    let live = true;
    setState({ status: "loading", surface: null, page: null });
    if (!slugParam && idParam) {
      // Builder-parity path: the saved landing page by event id. A 404 / fetch
      // failure falls through to the event's DEFAULT template below (exactly
      // what the builder shows for an undesigned event) — never a dead end.
      ApiClient.getLandingPage(idParam, studioRequestOptions("landing-pages"))
        .then((p) => { if (live) setState({ status: "ok", surface: null, page: (p ?? {}) as Record<string, any> }); })
        .catch((e: unknown) => { console.warn("getLandingPage (event preview) failed — using the event's default template:", e); if (live) setState({ status: "ok", surface: null, page: {} }); });
      return () => { live = false; };
    }
    // getPublishedLandingSurface resolves against the real @v0y4ge/api-client
    // (MSW mock in preview, real backend on deploy). Direct typed call — the
    // rebuilt gate (browser-gate-v1-72a3c35f-2026-07-08T13-48-00-903Z) carries
    // this op in its embedded type surface.
    ApiClient.getPublishedLandingSurface(slugParam, studioRequestOptions("landing-pages"))
      .then((s) => { if (live) setState({ status: "ok", surface: s as PublishedSurface, page: null }); })
      .catch((e: unknown) => { console.warn("getPublishedLandingSurface failed:", e); if (live) setState({ status: "error", surface: null, page: null }); });
    return () => { live = false; };
  }, [slugParam, idParam]);

  if (state.status === "loading") return <div style={{ minHeight: "100%", background: T.canvas, padding: 80, textAlign: "center", color: T.soft, fontSize: 15 }}>{t("common.status.loading", "Loading…")}</div>;

  // ── eventId path — the builder step's render, verbatim ──
  if (state.page) {
    const p = state.page;
    const savedBlocks = Array.isArray(p.blocks) && p.blocks.length ? (p.blocks as LandingBlock[]) : null;
    if (!savedBlocks && !row) {
      if (eventsLoading) return <div style={{ minHeight: "100%", background: T.canvas, padding: 80, textAlign: "center", color: T.soft, fontSize: 15 }}>{t("common.status.loading", "Loading…")}</div>;
      return <div style={{ minHeight: "100%", background: T.canvas, padding: 80, textAlign: "center", color: T.soft, fontSize: 15 }}>{t("hubs.publicHubs.thisPageIsnTAvailable", "This page isn't available.")}</div>;
    }
    // cfg-shaped record from the event row — the same fields store.loadEvent
    // hydrates the wizard from, so the derived template/hydration match the
    // builder opened on this event.
    const raw = (row?.raw ?? {}) as Record<string, any>;
    const cfgLike: Record<string, any> = row ? {
      title: row.title, description: row.description, label: row.template,
      start: raw.startsAt, end: raw.endsAt,
      location: row.location, venueName: raw.locationName || raw.location_name || "", address: raw.locationAddress || raw.location_address || "",
      heroImage: row.heroImage || "",
      tickets: Array.isArray(raw.tickets) ? raw.tickets : [],
      // WYSIWYG: the event's OWN registration config drives the capture form.
      registrationQuestions: Array.isArray(raw.registrationQuestions) ? raw.registrationQuestions : [],
    } : {};
    const blocks = savedBlocks ?? defaultLandingBlocks(cfgLike, row?.title || "Event");
    const themeId = (p.themeConfig?.id ?? p.themeConfig?.name ?? p.theme) as string | undefined;
    const theme: LandingTheme = (themeId ? LANDING_THEMES.find((x) => x.id === themeId || x.name === themeId) : undefined) || brandTheme || LANDING_THEMES[0];
    const font = (p.font ? String(p.font) : "") || theme.font;
    ensureLandingFont(font);
    const event: PublicEventDetailLike = ((p.event as PublicEventDetailLike | null) ?? (row ? previewEventFromCfg(cfgLike) : {}));
    const slugVal = (p.slug ? String(p.slug) : "") || String(raw.slug || "");
    const mode = p.status ? (String(p.status) === "published" ? "live" as const : "draft" as const)
      : (row && (row.status === "live" || row.status === "upcoming") ? "live" as const : "draft" as const);
    // Req 1 (Option-2 docket): the rendered page is FULL-BLEED — nothing after
    // the builder re-constrains desktop to a max width.
    return (
      <PublicScrollPane>
        <LandingPreview blocks={blocks} theme={theme} font={font} slug={slugVal} event={event} chrome={false}
          mode={mode} eventId={idParam} onOpenAttend={() => { void openPublicEventPage(slugVal || idParam); }}/>
      </PublicScrollPane>
    );
  }

  if (state.status === "error" || !state.surface) return <div style={{ minHeight: "100%", background: T.canvas, padding: 80, textAlign: "center", color: T.soft, fontSize: 15 }}>{t("hubs.publicHubs.thisPageIsnTAvailable", "This page isn't available.")}</div>;

  // The published attend page IS the editor preview — the SAME LandingPreview,
  // the SAME saved block array, hydrated from the canonical `event`. No browser
  // chrome, no Upcoming/Locations/Series/About nav: what was designed in the
  // Landing builder is exactly what ships publicly (operator item 1 + WYSIWYG).
  const surface = state.surface;
  // Draft-landing mandate: key the banner + disabled registration off the
  // data layer's publishState; isPublished is the already-shipped fallback.
  const publishState = surface.publishState ?? (surface.isPublished === false ? "draft-preview" : "published");
  const mode = publishState === "published" ? "live" as const : "draft" as const;
  // Older/partially materialized rows can carry the published event projection
  // but an empty blocks_json array. Render the builder's canonical event-derived
  // default so the public page still has title, schedule, and registration.
  const eventCfg = (surface.event ?? {}) as Record<string, any>;
  const blocks = Array.isArray(surface.blocks) && surface.blocks.length
    ? surface.blocks as LandingBlock[]
    : defaultLandingBlocks(eventCfg, String(eventCfg.title || "Event"));
  // Tenant brand theme (voyage#59): the surface's own brand kit first, then the
  // fetched /v1/branding/theme record; the design-time preset only as the last
  // honest fallback when no tenant kit exists.
  const theme: LandingTheme = themeFromBrandKit(surface.brandKit ?? surface.branding) ?? brandTheme ?? LANDING_THEMES[0];
  const font = surface.event?.font || theme.font;
  ensureLandingFont(font);
  const previewEvent: PublicEventDetailLike = { ...(surface.event ?? {}), venueGeo: surface.venueGeo ?? surface.event?.venueGeo ?? null };
  const eventId = String(surface.eventId ?? "") || undefined;
  return (
    // Req 1 (Option-2 docket): the attend page is FULL-BLEED — the builder's
    // render ships verbatim with no downstream max-width constraint.
    <PublicScrollPane>
      <LandingPreview blocks={blocks} theme={theme} font={font} slug={surface.slug || slugParam} event={previewEvent} chrome={false}
        mode={mode} eventId={eventId} onOpenAttend={() => { void openPublicEventPage(surface.slug || slugParam); }}/>
    </PublicScrollPane>
  );
}

export function PublicEventRegister() {
  // §2.5 follow-up (2026-07-26): same split as PublicEventLanding — the
  // anonymous visitor must not trigger the admin events cache (/v1/admin/
  // events). Signed-in Studio previewers keep the cache-backed record;
  // anonymous loads resolve the event via the ANONYMOUS public op
  // getPublicEventPage (GET /v1/public/events/{idOrSlug}).
  return isSignedIn() ? <RegisterViaAdminCache /> : <RegisterViaPublicLookup />;
}
function RegisterViaAdminCache() {
  const nav = useNav();
  const eventId = String((nav.params?.eventId || nav.params?.id) ?? "");
  // UIR-22: the event record comes from the real events hook — the legacy
  // window.__EVENTS__ side-channel is gone.
  const { events: uiEvents } = useEvents();
  const all = uiEvents as unknown as Record<string, unknown>[];
  const ev = all.find(e => String(e.id) === eventId) || all[0];
  return <EventRegisterBody ev={ev ?? null} />;
}
function RegisterViaPublicLookup() {
  const t = useT();
  const nav = useNav();
  const eventId = String((nav.params?.eventId || nav.params?.id) ?? "");
  const [state, setState] = useState<{ ev: Record<string, unknown> | null; status: "loading" | "ok" | "error" }>({ ev: null, status: "loading" });
  useEffect(() => {
    let live = true;
    ApiClient.getPublicEventPage(eventId)
      .then((p) => {
        if (!live) return;
        const r = p as unknown as Record<string, unknown>;
        setState({ status: "ok", ev: {
          id: r.id ?? eventId, title: r.title ?? r.name, hue: r.hue,
          capacity: r.capacity, registered: r.registered ?? r.registered_count,
          date: r.date ?? r.startsAt ?? r.starts_at, time: r.time,
          location: r.location ?? r.venueName ?? r.venue_name,
        } });
      })
      .catch((e: unknown) => { console.warn("getPublicEventPage failed:", e); if (live) setState({ status: "error", ev: null }); });
    return () => { live = false; };
  }, [eventId]);
  if (state.status === "loading") return <div style={{ minHeight: "100%", background: T.canvas, padding: 80, textAlign: "center", color: T.soft, fontSize: 15 }}>{t("common.status.loading", "Loading…")}</div>;
  if (state.status === "error" || !state.ev) return <div style={{ minHeight: "100%", background: T.canvas, padding: 80, textAlign: "center", color: T.soft, fontSize: 15 }}>{t("hubs.publicHubs.thisPageIsnTAvailable", "This page isn't available.")}</div>;
  return <EventRegisterBody ev={state.ev} />;
}
function EventRegisterBody({ ev }: { ev: Record<string, unknown> | null }) {
  const t = useT();
  const nav = useNav();
  const eventId = String((nav.params?.eventId || nav.params?.id) ?? "");
  const hue = String(ev?.hue || T.p1);
  const cap = Number(ev?.capacity) || 0;
  const reg = Number(ev?.registered) || 0;
  const soldOut = Math.max(0, cap - reg) === 0;
  const isWaitlist = nav.params?.mode === "waitlist" || soldOut;
  const slug = String(ev?.title || "event").toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/(^-|-$)/g, "");
  /* REQ-015 (open) — real ticket tiers pending; registration proceeds with no
   * priced tier (ticketId undefined) until the real tickets op ships */
  const tickets: { id: string; name: string; price: number; desc: string }[] = [];
  const [step, setStep] = useState(2);
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [confirmation, setConfirmation] = useState<{ confirmationNumber: string } | null>(null);
  const [ticketId, setTicketId] = useState<string | undefined>(undefined);
  const ticket = tickets.find(t => t.id === ticketId);
  const [form, setForm] = useState({ firstName: "", lastName: "", email: "", phone: "", company: "", termsOk: false });
  const [touched, setTouched] = useState<Record<string, boolean>>({});
  const [submitTried, setSubmitTried] = useState(false);
  const errors: Record<string, string> = {};
  if (!form.firstName.trim()) errors.firstName = "First name is required.";
  if (!form.lastName.trim()) errors.lastName = "Last name is required.";
  if (!/^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/.test(form.email.trim())) errors.email = "Enter a valid email.";
  if (form.phone.replace(/\D/g, "").length < 10) errors.phone = "Enter a complete phone number.";
  const formValid = !Object.keys(errors).length && form.termsOk;
  const errFor = (k: string) => (touched[k] || submitTried) ? errors[k] : undefined;
  const update = (k: string, v: unknown) => setForm(f => ({ ...f, [k]: v }));
  /* REQ-016 (open) \u2014 real submitPublicEventRegistration call, awaited, real
   * error state, confirmation number sourced from the response. No fake
   * success: without the op, this shows a "not available yet" error rather
   * than a fabricated confirmation (no-fake-interactivity mandate). */
  const handleSubmit = async () => {
    setSubmitTried(true);
    setTouched({ firstName: true, lastName: true, email: true, phone: true });
    if (!formValid || submitting) return;
    setSubmitting(true);
    setSubmitError(null);
    const op = (ApiClient as unknown as Record<string, unknown>).submitPublicEventRegistration;
    if (typeof op !== "function") {
      setSubmitting(false);
      setSubmitError("Registration isn't available yet on this preview (submitPublicEventRegistration — REQ-016). Nothing was submitted.");
      return;
    }
    try {
      const res = await (op as (i: unknown) => Promise<unknown>)({
        eventId: String(ev?.id || ""), ticketId,
        firstName: form.firstName, lastName: form.lastName, email: form.email, phone: form.phone, company: form.company,
        waitlist: isWaitlist,
      }) as Record<string, any>;
      setSubmitting(false);
      const num = res?.confirmationNumber ?? res?.registration?.confirmationNumber;
      if (!num) { setSubmitError("Registration didn't return a confirmation. Please try again."); return; }
      setConfirmation({ confirmationNumber: String(num) });
      setStep(3);
    } catch (e) {
      console.warn("submitPublicEventRegistration failed:", e);
      setSubmitting(false);
      setSubmitError("Couldn't complete your registration. Nothing was submitted — please try again.");
    }
  };
  return (
    <BrowserChrome url={composePublicUrl(`${slug}/register`, { bare: true })}>
      <PublicNav active="Upcoming"/>
      <section style={{ background: T.canvas, padding: "32px 64px 64px", minHeight: "calc(100% - 80px)" }}>
        <div style={{ maxWidth: 1000, margin: "0 auto" }}>
          {/* variant="link" leaves the background to the CSS state layer, which is what
              enables its hover underline; an inline `background: transparent` would win
              over the hover rule and kill it. fontWeight 400 keeps the UA default weight
              a bare <button> had (Btn's link tier defaults to 500). */}
          <Btn variant="link" onClick={() => nav.go("event.public", { eventId: String(ev?.id || "") })} style={{ border: "none", color: T.soft, fontSize: 13, fontWeight: 400, fontFamily: T.font, cursor: "pointer", display: "inline-flex", alignItems: "center", gap: 6, marginBottom: 18, padding: 0, height: "auto" }}>
            <Icon.arrow size={11} dir="left"/> {t("hubs.publicHubs.backToEventDetails", "Back to event details")}</Btn>
          <h1 style={{ fontSize: 32, fontWeight: 800, letterSpacing: -0.6, color: T.n1, margin: "0 0 6px" }}>{step === 3 ? (isWaitlist ? "You're on the waitlist" : "You're registered!") : isWaitlist ? t('hubs.publicHubs.text4') : t('hubs.publicHubs.text3')}</h1>
          <p style={{ fontSize: 15, color: T.soft, margin: "0 0 24px" }}>{t("hubs.publicHubs.for", "For")}{" "}<strong style={{ color: T.n1 }}>{String(ev?.title || "")}</strong> · {String(ev?.date || "")} · {String(ev?.time || "")}</p>
          <div style={{ display: "grid", gridTemplateColumns: step === 3 ? "1fr" : "1.5fr 1fr", gap: 28 }}>
            <div>
              {step === 2 ? (
                <Card style={{ padding: 24 }}>
                  <h2 style={{ fontSize: 18, fontWeight: 700, color: T.n1, margin: "0 0 4px" }}>{t("hubs.publicHubs.yourDetails", "Your details")}</h2>
                  <p style={{ fontSize: 13, color: T.soft, margin: "0 0 16px" }}>{t("hubs.publicHubs.weLlSendYourConfirmationHereRequired", "We'll send your confirmation here. * required.")}</p>
                  <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 }}>
                    <Input label={t("hubs.publicHubs.firstName", "First name *")} value={form.firstName} placeholder={t("hubs.publicHubs.jordan", "Jordan")} onChange={e => update("firstName", e.target.value)} onBlur={() => setTouched(t => ({ ...t, firstName: true }))} error={errFor("firstName")}/>
                    <Input label={t("hubs.publicHubs.lastName", "Last name *")}  value={form.lastName}  placeholder={t("hubs.publicHubs.rivera", "Rivera")}  onChange={e => update("lastName",  e.target.value)} onBlur={() => setTouched(t => ({ ...t, lastName:  true }))} error={errFor("lastName")}/>
                    <Input label={t("hubs.publicHubs.email", "Email *")} value={form.email} type="email" placeholder={t("hubs.publicHubs.nameCompanyCom", "name@company.com")} onChange={e => update("email", e.target.value.trim())} onBlur={() => setTouched(t => ({ ...t, email: true }))} error={errFor("email")}/>
                    <Input label={t("hubs.publicHubs.phone", "Phone *")} value={form.phone} type="tel" placeholder="(555) 555-0123" onChange={e => update("phone", e.target.value)} onBlur={() => setTouched(t => ({ ...t, phone: true }))} error={errFor("phone")}/>
                    <Input label={t("hubs.publicHubs.company", "Company")} value={form.company} onChange={e => update("company", e.target.value)}/>
                  </div>
                  <div style={{ marginTop: 14, display: "flex", flexDirection: "column", gap: 10 }}>
                    <label style={{ display: "flex", alignItems: "flex-start", gap: 10, fontSize: 13, color: form.termsOk ? T.n2 : submitTried ? T.red1 : T.n2, cursor: "pointer" }}>
                      <Checkbox checked={form.termsOk} onChange={() => update("termsOk", !form.termsOk)}/>
                      <span>{t("hubs.publicHubs.iAgreeToThe", "I agree to the")}{" "}<a href="#" style={{ color: T.p1, fontWeight: 600 }}>{t("hubs.publicHubs.eventTerms", "event terms")}</a> {t("hubs.publicHubs.and", "and")}{" "}<a href="#" style={{ color: T.p1, fontWeight: 600 }}>{t("hubs.publicHubs.privacyPolicy", "privacy policy")}</a>. *</span>
                    </label>
                  </div>
                  <div style={{ marginTop: 20, display: "flex", justifyContent: "flex-end" }}>
                    <Btn onClick={handleSubmit} disabled={submitting || (!formValid && submitTried)} icon={<Icon.check size={14}/>}>{submitting ? t("hubs.publicHubs.submitting", "Submitting…") : isWaitlist ? t("hubs.publicHubs.joinWaitlist", "Join waitlist") : t("hubs.publicHubs.completeRegistration", "Complete registration")}</Btn>
                  </div>
                  {submitError ? <div style={{ marginTop: 10, padding: "10px 12px", background: T.red2, border: `1px solid ${T.red1}`, borderRadius: T.radius.control, fontSize: 13, color: T.n1 }}>{submitError}</div> : null}
                </Card>
              ) : (
                <Card style={{ padding: 32, textAlign: "center" }}>
                  <div style={{ width: 64, height: 64, borderRadius: T.radius.pill, background: T.green1, color: T.white, display: "inline-flex", alignItems: "center", justifyContent: "center", margin: "0 auto 18px" }}><Icon.check size={28}/></div>
                  <h2 style={{ fontSize: 26, fontWeight: 800, color: T.n1, margin: "0 0 8px" }}>{isWaitlist ? t('hubs.publicHubs.text5') : t('hubs.publicHubs.text6')}</h2>
                  <p style={{ fontSize: 15, color: T.soft, margin: "0 auto 24px", maxWidth: 520 }}>{t("hubs.publicHubs.confirmationSentTo", "Confirmation sent to")}{" "}<strong style={{ color: T.n1 }}>{form.email}</strong>.</p>
                  <div style={{ display: "inline-flex", flexDirection: "column", gap: 4, padding: "12px 18px", background: T.n6, borderRadius: T.radius.md, border: `1px solid ${T.border}`, marginBottom: 24 }}>
                    <span style={{ fontSize: 11, color: T.soft, textTransform: "uppercase", letterSpacing: 0.5, fontWeight: 600 }}>{t("hubs.publicHubs.confirmation", "Confirmation #")}</span>
                    <span style={{ fontFamily: T.fontMono, fontSize: 14, color: T.n1, fontWeight: 600 }}>{confirmation?.confirmationNumber ?? "—"}</span>
                  </div>
                  <div style={{ display: "flex", gap: 10, justifyContent: "center", flexWrap: "wrap" }}>
                    <Btn icon={<Icon.calendar size={14}/>} onClick={() => { downloadIcs({ id: String(ev?.id || ""), title: String(ev?.title || ""), date: String(ev?.date || ""), time: String(ev?.time || ""), location: String(ev?.location || "") }); toast({ tone: "success", title: t("hubs.publicHubs.calendarFileDownloaded", "Calendar file downloaded") }); }}>{t("hubs.publicHubs.addToCalendar", "Add to calendar")}</Btn>
                    <Btn variant="secondary" icon={<Icon.link size={14}/>} onClick={() => { navigator.clipboard.writeText(composePublicUrl(slug, { bare: true })).then(() => toast({ tone: "success", title: t("hubs.publicHubs.eventLinkCopied", "Event link copied") })).catch(() => toast({ tone: "danger", title: t("hubs.publicHubs.couldnTCopyTheLink", "Couldn't copy the link") })); }}>{t("hubs.publicHubs.shareEvent", "Share event")}</Btn>
                    <Btn variant="ghost" onClick={() => nav.go("event.public", { eventId: String(ev?.id || "") })}>{t("hubs.publicHubs.backToEvent", "Back to event")}</Btn>
                  </div>
                </Card>
              )}
            </div>
            {step < 3 ? (
              <aside style={{ alignSelf: "start", position: "sticky", top: 24 }}>
                <Card style={{ padding: 0, overflow: "hidden" }}>
                  <div style={{ background: `linear-gradient(135deg, ${hue}, ${T.p1})`, color: T.white, padding: "20px 22px" }}>
                    <div style={{ fontSize: 11, fontWeight: 700, textTransform: "uppercase", letterSpacing: 1, opacity: 0.85 }}>{String(ev?.template || "Event")}</div>
                    <div style={{ fontSize: 17, fontWeight: 700, marginTop: 6 }}>{String(ev?.title || "")}</div>
                  </div>
                  <div style={{ padding: "16px 22px", display: "flex", flexDirection: "column", gap: 10, fontSize: 13 }}>
                    <div style={{ display: "flex", alignItems: "center", gap: 8 }}><Icon.calendar size={13} style={{ color: T.soft }}/>{String(ev?.date || "")} · {String(ev?.time || "")}</div>
                    <div style={{ display: "flex", alignItems: "center", gap: 8 }}><Icon.loc size={13} style={{ color: T.soft }}/>{String(ev?.location || "")}</div>
                  </div>
                  <div style={{ borderTop: `1px solid ${T.border}`, padding: "14px 22px" }}>
                    <div style={{ display: "flex", justifyContent: "space-between", fontSize: 13, color: T.n1 }}>
                      <span>{ticket?.name ?? "Registration"} × 1</span>
                      <span style={{ fontWeight: 600 }}>{!ticket || ticket.price === 0 ? t('hubs.publicHubs.text7') : /* TODO(i18n): replace template-literal with t('hubs.publicHubs.text8', { ... }) */`$${ticket.price}`}</span>
                    </div>
                    <div style={{ borderTop: `1px solid ${T.border}`, paddingTop: 8, marginTop: 8, display: "flex", justifyContent: "space-between", fontSize: 15, color: T.n1, fontWeight: 700 }}>
                      <span>{t("hubs.publicHubs.total", "Total")}</span><span>{!ticket || ticket.price === 0 ? t('hubs.publicHubs.text7') : /* TODO(i18n): replace template-literal with t('hubs.publicHubs.text9', { ... }) */`$${ticket.price + 2.5}`}</span>
                    </div>
                  </div>
                </Card>
              </aside>
            ) : null}
          </div>
        </div>
      </section>
      <PublicFooter/>
    </BrowserChrome>
  );
}
